import { KEYBINDS } from '../../config/constants.js';
import { isMobileModeActive } from '../core/deviceMode.js';

// How long the in-arena hint stays up from the first tick of a run, and how
// much of that tail is spent fading out. Long enough to read twice at a glance,
// short enough that it's gone before the first wave gets busy.
export const CONTROLS_HINT_DURATION = 8;
export const CONTROLS_HINT_FADE = 2;

// KeyboardEvent.code -> the short label the hint shows. Deliberately a smaller
// map than ControlsMenu's: the hint names ONE binding per action (the primary),
// not every alias, because a bar reading "Shift / Shift — Dash" teaches nothing.
const PRIMARY_KEY_LABELS = {
  KeyW: 'WASD', ShiftLeft: 'Shift', Space: 'Space', Escape: 'Esc', KeyC: 'C',
};

function primaryKey(codes, fallback) {
  return PRIMARY_KEY_LABELS[codes[0]] ?? fallback;
}

/**
 * The one-line controls reminder drawn across the bottom of the arena at the
 * start of a run (ARCHIVE.md P4). The game explained none of itself before
 * this: a new player's first death came without ever being told that dash
 * exists, that it has i-frames, or that weapons fire on their own.
 *
 * Reads the same KEYBINDS the ControlsMenu does, so a rebind can never leave
 * the hint lying, and switches to the on-screen button glyphs whenever the
 * touch controls are the ones actually mounted (core/deviceMode.js).
 */
export function controlsHintText(save) {
  if (isMobileModeActive(save)) {
    return 'JOYSTICK MOVE  ·  ⚡ DASH  ·  ◎ FOCUS  ·  ★ ULTIMATE  ·  ❙❙ PAUSE  ·  WEAPONS FIRE THEMSELVES';
  }
  const move = primaryKey(KEYBINDS.up, 'WASD');
  const dash = primaryKey(KEYBINDS.dash, 'Shift');
  const focus = primaryKey(KEYBINDS.focus, 'C');
  const ultimate = primaryKey(KEYBINDS.ultimate, 'Space');
  const pause = primaryKey(KEYBINDS.pause, 'Esc');
  // "HOLD C FOCUS" rather than "C FOCUS": focus is the only binding here that
  // is worthless tapped, and the hint is the only place that gets said.
  return `${move} MOVE  ·  ${dash} DASH  ·  HOLD ${focus} FOCUS  ·  ${ultimate} ULTIMATE  ·  ${pause} PAUSE  ·  WEAPONS FIRE THEMSELVES`.toUpperCase();
}

/**
 * Just the ultimate's label, for cues that have to name the key inline —
 * the deathbomb window prompt (BULLET-HELL.md §D3). Shares the hint's
 * rebind-awareness and its touch glyph, so a prompt can't tell the player
 * to press a key they don't have.
 */
export function ultimateKeyLabel(save) {
  return isMobileModeActive(save) ? '★' : primaryKey(KEYBINDS.ultimate, 'Space').toUpperCase();
}

/** Hint opacity at `elapsed` seconds into the run; 0 once it has fully faded. */
export function controlsHintAlpha(elapsed) {
  if (elapsed >= CONTROLS_HINT_DURATION) return 0;
  const remaining = CONTROLS_HINT_DURATION - elapsed;
  if (remaining >= CONTROLS_HINT_FADE) return 1;
  return remaining / CONTROLS_HINT_FADE;
}
